import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { Amenity } from "@/types/amenity";
import { amenityService } from "@/services/amenity.service";

interface AmenityState {
  amenities: Amenity[];
  loading: boolean;
  error: string | null;
}

const initialState: AmenityState = {
  amenities: [],
  loading: false,
  error: null,
};

// Async thunk to fetch amenities
export const fetchAmenities = createAsyncThunk(
  "amenity/fetchAmenities",
  async (bookingId: number | string, { rejectWithValue }) => {
    try {
      const response = await amenityService.getAmenities(bookingId);
      if (!response.errors || response.errors.length === 0) {
        return response.result;
      } else {
        return rejectWithValue(response.errors.join(", "));
      }
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

// Async thunk to fetch room type amenities
export const fetchRoomTypeAmenities = createAsyncThunk(
  "amenity/fetchRoomTypeAmenities",
  async (roomTypeId: number, { rejectWithValue }) => {
    try {
      const response = await amenityService.getRoomTypeAmenities(roomTypeId);
      // Always return the result, even if there are no amenities or errors
      // This prevents unnecessary rejections for optional amenities
      return response.result || [];
    } catch (error) {
      return rejectWithValue(
        error instanceof Error ? error.message : "Unknown error",
      );
    }
  },
);

const amenitySlice = createSlice({
  name: "amenity",
  initialState,
  reducers: {
    clearAmenities: (state) => {
      state.amenities = [];
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch amenities
      .addCase(fetchAmenities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchAmenities.fulfilled, (state, action) => {
        state.loading = false;
        state.amenities = action.payload;
        state.error = null;
      })
      .addCase(fetchAmenities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.amenities = [];
      })
      // Fetch room type amenities
      .addCase(fetchRoomTypeAmenities.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchRoomTypeAmenities.fulfilled, (state, action) => {
        state.loading = false;
        state.amenities = action.payload;
        state.error = null;
      })
      .addCase(fetchRoomTypeAmenities.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.amenities = [];
      });
  },
});

export const { clearAmenities } = amenitySlice.actions;
export default amenitySlice.reducer;
